import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { LanguageContext } from '../contexts/LanguageContext';
import ContactBloc from '../components/ContactBloc';
import devTyping from '../img/assets/dev-typing.png';
import devGuitar from '../img/assets/dev-guitar.png';
import '../styles/About.css';

const About: React.FC = () => {
    const { language } = useContext(LanguageContext)!;

    const textPart1 = language === 'fr' ? (
        <>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>Depuis mes débuts, une constante a marqué toutes mes expériences professionnelles : l'importance de l'humain. Que ce soit en tant que chargé d'événements, acheteur pour de grandes entreprises, concepteur de sites internet, ou même directeur artistique en production musicale, j'ai toujours vu chaque projet comme un groupe de musique. Si l'un des membres n'a pas la liberté d'exprimer pleinement son talent, c'est l'ensemble qui en souffre.</motion.p>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>Cette vision m'a poussé à développer une approche collaborative, où chaque acteur a sa place et son importance. Je crois fermement que la communication claire et l'empathie sont au cœur de tout succès, et je fais de ces valeurs mes piliers dans chaque projet.</motion.p>
        </>
    ) : (
        <>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>Since the beginning of my career, a constant theme has marked all my professional experiences: the importance of the human element. Whether as an event manager, buyer for large companies, web designer, or artistic director in music production, I’ve always viewed each project like a music group. If one member is not free to fully express their talent, the entire group suffers.</motion.p>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>This vision led me to develop a collaborative approach, where each participant has their place and importance. I firmly believe that clear communication and empathy are at the heart of every success, and I make these values central to every project.</motion.p>
        </>
    );

    const textPart2 = language === 'fr' ? (
        <>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>Passionné par les nouvelles technologies et animé par une curiosité insatiable, je m'engage à relever les défis les plus complexes avec une approche alliant performance et sécurité. Chaque solution que je propose est soigneusement pensée pour répondre aux besoins spécifiques de mes clients, tout en plaçant toujours l'utilisateur final au centre du processus.</motion.p>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>Mon parcours, riche et varié, m'a appris que la clé du succès repose sur l'organisation, la curiosité, et surtout, l'esprit d'équipe. Qu'il s'agisse de gérer des projets de développement web ou de concevoir des expériences utilisateur optimisées, je m'efforce d'apporter une vision globale, mais humaine, à chaque projet.</motion.p>
        </>
    ) : (
        <>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>Passionate about new technologies and driven by insatiable curiosity, I am committed to tackling the most complex challenges with an approach that balances performance and security. Each solution I propose is carefully crafted to meet my clients’ specific needs, while always placing the end user at the center of the process.</motion.p>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>My rich and varied career has taught me that the key to success lies in organization, curiosity, and above all, teamwork. Whether managing web development projects or designing optimized user experiences, I strive to bring a holistic yet human perspective to every project.</motion.p>
        </>
    );

    const headerVariants = {
        hidden: { opacity: 0, y: -20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5 }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="about"
        >
            <motion.div className="about-header" variants={headerVariants} initial="hidden" animate="visible">
                <p>{language === 'fr' ? 'À Propos' : 'About'}</p>
                <h1>{language === 'fr' ? `Plus d'informations sur mon parcours` : `More information about my career`}</h1>
            </motion.div>

            <motion.div className="about-section" initial="hidden" animate="visible">
                <div className="about-text">{textPart1}</div>
                <motion.img
                    className="about-image"
                    src={devTyping}
                    alt="Developer typing illustration"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.7 }}
                    loading="lazy"
                />
            </motion.div>

            <motion.div className="about-section about-second" initial="hidden" animate="visible">
                <motion.img
                    className="about-image"
                    src={devGuitar}
                    alt="Musician playing music"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.7 }}
                    loading="lazy"
                />
                <div className="about-text">{textPart2}</div>
            </motion.div>

            <ContactBloc />
        </motion.div>
    );
}

export default About;
